import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Auftrag,
  AuftragAssignment,
  AuftragAssignmentCreate,
  EinsatzMaterial,
  PlannableUser,
  StammkundeWorker
} from '../../../Models/stammkunde.model';

export interface EinsatzFormData {
  auftrag: Auftrag;
  assignment?: AuftragAssignment;
  /** Login-Team-Mitglieder des Stammkunden (Verantwortlicher/Beteiligte). */
  users: PlannableUser[];
  /** Arbeiter ohne Login im Team des Stammkunden. */
  workers?: StammkundeWorker[];
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf einen Tag). */
  prefillDate?: string;
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf eine Personen-Zeile). */
  prefillUserId?: string;
  /** Vorbelegung aus dem Planungsboard (Drag & Drop auf eine Arbeiter-Zeile). */
  prefillWorkerId?: number;
}

/**
 * Dialog zum Planen eines Einsatzes (wer, wann, wo) für einen Stammkunden-Auftrag.
 * Optional wird direkt eine Outlook-/Kalender-Einladung (.ics) verschickt.
 */
@Component({
  selector: 'app-einsatz-form',
  templateUrl: './einsatz-form.component.html',
  styleUrls: ['./einsatz-form.component.css', '../stammkunden-theme.css']
})
export class EinsatzFormComponent {
  isEdit: boolean;
  saving = false;

  assignedUserId = '';
  participantUserIds: string[] = [];
  workerIds: number[] = [];
  materials: EinsatzMaterial[] = [];
  title = '';
  startDate = '';
  startTime = '07:00';
  endTime = '16:00';
  location = '';
  notes = '';
  // Standardmäßig KEINE Kalender-Einladung/E-Mail bei Stammkunden-Terminen (bewusst ankreuzbar).
  sendInvite = false;

  constructor(
    public dialogRef: MatDialogRef<EinsatzFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EinsatzFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.assignment;
    const a = data.assignment;
    if (a) {
      this.assignedUserId = a.assignedUserId;
      this.participantUserIds = [...a.participantUserIds];
      this.workerIds = [...(a.workerIds ?? [])];
      this.materials = (a.materials ?? []).map(m => ({ ...m }));
      this.title = a.title;
      const start = new Date(a.start);
      const end = new Date(a.end);
      this.startDate = this.toDateInput(start);
      this.startTime = this.toTimeInput(start);
      this.endTime = this.toTimeInput(end);
      this.location = a.location ?? '';
      this.notes = a.notes ?? '';
      this.sendInvite = false;
    } else {
      this.title = data.auftrag.title;
      this.location = data.auftrag.location ?? '';
      const base = data.auftrag.startDate ? new Date(data.auftrag.startDate) : new Date();
      this.startDate = data.prefillDate ?? this.toDateInput(base);
      if (data.prefillUserId) {
        this.assignedUserId = data.prefillUserId;
      }
      if (data.prefillWorkerId != null) {
        this.workerIds = [data.prefillWorkerId];
      }
    }
  }

  get valid(): boolean {
    return !!this.assignedUserId && !!this.startDate && !!this.startTime && !!this.endTime
      && this.endTime > this.startTime;
  }

  // ── Material (Dokumentation, ohne Preise) ──

  addMaterial(): void {
    this.materials.push({ name: '', quantity: 1, unit: 'Stk' });
  }

  removeMaterial(index: number): void {
    this.materials.splice(index, 1);
  }

  /** Zeit-Schnellwahl (Ganztag/Vormittag/Nachmittag). */
  setTimes(start: string, end: string): void {
    this.startTime = start;
    this.endTime = end;
  }

  isTimes(start: string, end: string): boolean {
    return this.startTime === start && this.endTime === end;
  }

  save(): void {
    if (!this.valid) return;
    this.saving = true;

    const payload: AuftragAssignmentCreate = {
      assignedUserId: this.assignedUserId,
      participantUserIds: this.participantUserIds,
      workerIds: this.workerIds,
      materials: this.materials
        .filter(m => m.name.trim())
        .map(m => ({ name: m.name.trim(), quantity: m.quantity || 0, unit: m.unit?.trim() || 'Stk' })),
      title: this.title.trim() || undefined,
      start: `${this.startDate}T${this.startTime}:00`,
      end: `${this.startDate}T${this.endTime}:00`,
      location: this.location.trim() || undefined,
      notes: this.notes.trim() || undefined,
      sendInvite: this.sendInvite
    };

    const request$ = this.isEdit
      ? this.stammkundeService.updateAssignment(this.data.auftrag.id, this.data.assignment!.id, payload)
      : this.stammkundeService.createAssignment(this.data.auftrag.id, payload);

    request$.subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: err => {
        this.saving = false;
        const message = (typeof err?.error === 'string' && err.error)
          || err?.error?.message
          || 'Fehler beim Speichern des Einsatzes.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  userLabel(user: PlannableUser): string {
    return user.userName || user.email || user.id;
  }

  private toDateInput(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private toTimeInput(date: Date): string {
    const h = `${date.getHours()}`.padStart(2, '0');
    const min = `${date.getMinutes()}`.padStart(2, '0');
    return `${h}:${min}`;
  }
}
